import { forwardRef, useEffect, useMemo, useState } from "react";
import { submitSymptoms } from "../../api/forms/symptoms";
import {
    RPEScales,
    Symptoms,
    SymptomsTypes,
} from "../../api/forms/symptoms/types";
import { Nullable } from "../../api/types";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import { FormHandle, InputTypes } from "../../types";
import { generateSelectOptions, onFormValuesChange } from "../../utils";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";
import _ from "lodash";
import { twMerge } from "tailwind-merge";

const SYMPTOMS_TYPES_TEXTS = {
    [SymptomsTypes.NONE]: "None",
    [SymptomsTypes.DYSPNEA]: "Dyspnea",
    [SymptomsTypes.CHEST_PAIN]: "Chest pain",
    [SymptomsTypes.LEG_PAIN]: "Leg pain",
    [SymptomsTypes.GENERAL_FATIGUE]: "General fatigue",
};

export default forwardRef<
    FormHandle<Nullable<Symptoms>> | null,
    {
        disabled?: boolean;
        withECG: boolean;
        withSymptoms: boolean;
        dense?: boolean;
        isInterpretationScreen?: boolean;
        slots?: {
            afterECG?: React.ReactNode;
        };
        classes?: {
            root?: string;
            symptomsClasses?: DataInputGenericFormProps<Symptoms>["classes"];
            ecgClasses?: DataInputGenericFormProps<Symptoms>["classes"];
        };
    }
>(function SymptomsForm(
    props,
    ref: React.ForwardedRef<FormHandle<Nullable<Symptoms>> | null>
) {
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.SYMPTOMS,
    });

    const [formValues, setFormValues] = useState<Nullable<Symptoms>>();

    useEffect(() => {
        if (formData) {
            setFormValues(formData as Symptoms);
        }
    }, [formData]);

    const { valuesErrors, submissionLoading } =
        useImperativeFormSubmit<Symptoms>({
            formValues,
            formData,
            ref,
            onSubmitSuccess: setFromData,
            submitFunction: submitSymptoms,
        });

    type FormFields = DataInputGenericFormProps<Symptoms>["formFields"];

    const ecgFormFields = useMemo<FormFields>((): FormFields => {
        if (!formValues || !formData) return [];
        const ecgOptions = formData.all_ecg_types.map((value) => ({
            id: value,
            value: value,
            label: value,
        }));
        return [
            {
                id: "rest_ecg",
                label: "Rest",
                value: formValues.rest_ecg,
                valueToDisplay: formValues.rest_ecg.join(", "),
                originalValue: formData.rest_ecg,
                type: InputTypes.SELECT,
                error: valuesErrors?.rest_ecg,
                options: ecgOptions,
                multiple: true,
                required: false,
                disabled: props.disabled,
            },
            {
                id: "exercise_ecg",
                label: "Exercise",
                value: formValues.exercise_ecg,
                valueToDisplay: formValues.exercise_ecg.join(", "),
                originalValue: formData.exercise_ecg,
                type: InputTypes.SELECT,
                error: valuesErrors?.exercise_ecg,
                options: ecgOptions,
                multiple: true,
                required: false,
                disabled: props.disabled,
            },
            {
                id: "recovery_ecg",
                label: "Recovery",
                value: formValues.recovery_ecg,
                valueToDisplay: formValues.recovery_ecg.join(", "),
                originalValue: formData.recovery_ecg,
                type: InputTypes.SELECT,
                error: valuesErrors?.recovery_ecg,
                options: ecgOptions,
                multiple: true,
                required: false,
                disabled: props.disabled,
            },
        ];
    }, [
        formValues,
        formData,
        valuesErrors?.rest_ecg,
        valuesErrors?.exercise_ecg,
        valuesErrors?.recovery_ecg,
        props.disabled,
    ]);

    const formFields = useMemo<FormFields>(
        (): FormFields =>
            !formValues || !formData
                ? []
                : [
                      {
                          id: "type",
                          label: "Type",
                          value: formValues.type,
                          valueToDisplay: formValues.type.join(", "),
                          originalValue: formData.type,
                          type: InputTypes.SELECT,
                          error: valuesErrors?.type,
                          options: generateSelectOptions({
                              enum: SymptomsTypes,
                              texts: SYMPTOMS_TYPES_TEXTS,
                          }),
                          multiple: true,
                          required: false,
                          disabled: props.disabled,
                      },
                      {
                          id: "rpe",
                          scale: {
                              id: "rpeScale",
                              label: "RPE",
                              value: formValues.type.includes(
                                  SymptomsTypes.NONE
                              )
                                  ? null
                                  : formValues.rpeScale,
                              originalValue: formData.rpeScale,
                              type: InputTypes.RADIO,
                              options: generateSelectOptions({
                                  enum: RPEScales,
                                  texts: {
                                      [RPEScales.TEN]: "1-10",
                                      [RPEScales.TWENTY]: "6-20",
                                  },
                              }),
                              error: valuesErrors?.rpeScale,
                              required: false,
                              isSmallInput: true,
                              disabled:
                                  formValues.type.includes(
                                      SymptomsTypes.NONE
                                  ) ||
                                  !formValues.type ||
                                  props.disabled,
                          },
                          value: {
                              id: "rpeValue",
                              label: "RPE Value",
                              value: formValues.type.includes(
                                  SymptomsTypes.NONE
                              )
                                  ? null
                                  : formValues.rpeValue,
                              originalValue: formData.rpeValue,
                              type: InputTypes.SELECT,
                              options:
                                  formValues.rpeScale === RPEScales.TEN
                                      ? Array.from(
                                            { length: 10 },
                                            (_, i) => i + 1
                                        ).map((value) => ({
                                            id: value.toString(),
                                            value: value,
                                            label: value.toString(),
                                        }))
                                      : Array.from(
                                            { length: 15 },
                                            (_, i) => i + 6
                                        ).map((value) => ({
                                            id: value.toString(),
                                            value: value,
                                            label: value.toString(),
                                        })),
                              error: valuesErrors?.rpeValue,
                              required: false,
                              isSmallInput: true,
                              disabled:
                                  formValues.type.includes(
                                      SymptomsTypes.NONE
                                  ) ||
                                  !formValues.rpeScale ||
                                  props.disabled,
                          },
                      },
                  ],
        [
            formValues,
            formData,
            valuesErrors?.type,
            valuesErrors?.rpeScale,
            valuesErrors?.rpeValue,
            props.disabled,
        ]
    );

    if (error) {
        return <Error />;
    }

    return (
        <div
            className={twMerge(
                "flex flex-col",
                props.dense ? "gap-2.5 xl:gap-4" : "gap-2.5 xl:gap-8",
                props.classes?.root
            )}
        >
            {props.withECG ? (
                <DataInputGenericForm
                    formTitle="ECG Findings"
                    formFields={ecgFormFields}
                    formData={formValues}
                    onFromValuesChange={({ value, fieldSettings }) =>
                        onFormValuesChange({
                            value,
                            fieldSettings,
                            setFormValues,
                        })
                    }
                    loading={loading}
                    dense={props.dense}
                    classes={props.classes?.ecgClasses}
                />
            ) : null}
            {props.slots?.afterECG || null}
            {props.withSymptoms ? (
                <DataInputGenericForm
                    formTitle="Symptoms"
                    formFields={formFields}
                    formData={formValues}
                    onFromValuesChange={({ value, fieldSettings }) => {
                        const isRPEScaleChange =
                            fieldSettings.id === "rpeScale" &&
                            value !== formValues?.rpeScale;
                        const isSymptomsTypeChange =
                            fieldSettings.id === "type" &&
                            !_.isEqual(value, formValues?.type);

                        const customData = {};
                        if (isRPEScaleChange || isSymptomsTypeChange) {
                            Object.assign(customData, {
                                rpeValue: null,
                            });
                        }
                        onFormValuesChange({
                            value,
                            fieldSettings,
                            setFormValues,
                            customData,
                        });
                    }}
                    loading={loading || submissionLoading}
                    dense={props.dense}
                    isHorizontal={props.isInterpretationScreen}
                    classes={props.classes?.symptomsClasses}
                />
            ) : null}
        </div>
    );
});
